import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Trash2, Plus } from 'lucide-react';
import { Condition, CanvasElement } from './types';

interface ConditionsModalProps {
  visible: boolean;
  onHide: () => void;
  element: CanvasElement;
  onSave: (conditions: Condition[], logic: 'AND' | 'OR') => void;
  variables: any[];
}

export const ConditionsModal: React.FC<ConditionsModalProps> = ({
  visible,
  onHide,
  element,
  onSave,
  variables
}) => {
  const [conditions, setConditions] = React.useState<Condition[]>(element.visibleIf || []);
  const [logic, setLogic] = React.useState<'AND' | 'OR'>(element.visibleIfLogic || 'AND');

  React.useEffect(() => {
    if (visible) {
      setConditions(element.visibleIf || []);
      setLogic(element.visibleIfLogic || 'AND');
    }
  }, [visible, element]);

  const operators = [
    { label: 'Eşittir (==)', value: 'equals' },
    { label: 'Eşit Değildir (!=)', value: 'notEquals' },
    { label: 'İle Başlar', value: 'startsWith' },
    { label: 'İçerir', value: 'contains' },
    { label: 'Boştur', value: 'isEmpty' },
    { label: 'Dolu', value: 'isNotEmpty' },
    { label: 'SKU İçerir (Liste)', value: 'hasItemWithSku' }
  ];

  const handleAdd = () => {
    setConditions([...conditions, { field: '', operator: 'equals', value: '' }]);
  };

  const handleUpdate = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  const handleDelete = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
  };

  const handleSave = () => {
    // Clean empty
    const valid = conditions.filter(c => c.field && c.operator);
    onSave(valid, logic);
    onHide();
  };

  return (
    <Dialog header="Görünürlük Koşulları" visible={visible} style={{ width: '600px' }} onHide={onHide}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="İptal" icon="pi pi-times" onClick={onHide} className="p-button-text" />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} autoFocus />
        </div>
      }>
      <p className="m-0 mb-4 text-sm text-text/70">
        Bu nesnenin etiket üzerinde gösterilip gösterilmeyeceğini belirleyen koşulları ekleyin. Hiç koşul yoksa her zaman gösterilir.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded p-3 mb-4 text-xs text-text/80">
        <strong className="text-primary block mb-1">Örnek Kullanımlar:</strong>
        <ul className="list-disc pl-4 space-y-1">
          <li><strong>Alan:</strong> <code className="bg-bgAlt px-1 rounded">order.shipment</code> | <strong>Operatör:</strong> İle Başlar | <strong>Değer:</strong> <code className="bg-bgAlt px-1 rounded">YUR</code></li>
          <li><strong>Alan:</strong> <code className="bg-bgAlt px-1 rounded">order.description</code> | <strong>Operatör:</strong> Dolu (Bu koşul sağlanırsa nesne görünür)</li>
          <li><strong>Alan:</strong> <code className="bg-bgAlt px-1 rounded">physicalItems</code> | <strong>Operatör:</strong> SKU İçerir (Liste) | <strong>Değer:</strong> <code className="bg-bgAlt px-1 rounded">K</code></li>
        </ul>
      </div>

      {conditions.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          <label className="text-sm font-semibold text-text">Koşul Mantığı:</label>
          <Dropdown 
            value={logic} 
            options={[{label: 'Tümü Sağlanmalı (AND)', value: 'AND'}, {label: 'Herhangi Biri Sağlanmalı (OR)', value: 'OR'}]} 
            onChange={(e) => setLogic(e.value)} 
            className="w-48"
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        {conditions.map((cond, index) => (
          <div key={index} className="flex items-center gap-2 bg-bgAlt p-2 rounded border border-borderColor">
            <InputText 
              placeholder="Alan (örn: order.orderNumber)" 
              value={cond.field} 
              onChange={(e) => handleUpdate(index, { field: e.target.value })} 
              className="p-inputtext-sm flex-1 w-full"
            />
            <Dropdown 
              value={cond.operator} 
              options={operators} 
              onChange={(e) => handleUpdate(index, { operator: e.value })} 
              className="p-dropdown-sm w-48 shrink-0"
            />
            {cond.operator !== 'isEmpty' && cond.operator !== 'isNotEmpty' && (
               <InputText 
                 placeholder="Değer" 
                 value={cond.value || ''} 
                 onChange={(e) => handleUpdate(index, { value: e.target.value })} 
                 className="p-inputtext-sm flex-1 w-full"
               />
            )}
            <Button icon={<Trash2 className="w-4 h-4"/>} className="p-button-danger p-button-text p-2 hover:bg-danger/10 text-danger" onClick={() => handleDelete(index)} />
          </div>
        ))}

        <Button label="Koşul Ekle" icon={<Plus className="w-4 h-4 mr-2"/>} className="p-button-outlined p-button-sm w-32 mt-2" onClick={handleAdd} />
      </div>
    </Dialog>
  );
};
